import { toast } from "react-toastify";
import { TranslationOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Button, Form, Input, Modal, Table } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ManageTranslationItem {
  translation_id: number | null;
  translation_name: string;
  /** Only used when the modal is rendered with `withDescription`. */
  translation_description?: string;
  language_id: number;
  language_code: string;
  language_name?: string;
}

export interface ManageTranslationLanguage {
  id: number;
  language_code: string;
  name?: string;
}

interface ManageTranslationModalProps {
  open: boolean;
  title?: string;
  /** Shown under the title in the header, usually the entity name. */
  subtitle?: string;
  languages: ManageTranslationLanguage[];
  /** Existing translations of the entity; merged with `languages` into one row per language. */
  translations: ManageTranslationItem[];
  /** Show the table loading state while the parent is fetching translations. */
  loading?: boolean;
  /** Wait for the parent to finish fetching before hydrating the form. */
  ready?: boolean;
  /** Also manage a description per language (name stays the required/primary field). */
  withDescription?: boolean;
  /** Require a translation for every language. */
  requireAll?: boolean;
  /** Used as auto-translate source when no field has been filled in. */
  fallbackSourceText?: string;
  onCancel: () => void;
  /** Persist the items. Reject to keep the modal open; the modal closes on resolve. */
  onSave: (items: ManageTranslationItem[]) => Promise<void>;
  /** Enables the Auto translate button; resolves to `{ [language_code]: text }`. */
  onAutoTranslate?: (
    text: string,
    sourceLanguageCode: string
  ) => Promise<Record<string, string>>;
}

const fieldName = (languageId: number) => `translation_${languageId}`;
const descriptionFieldName = (languageId: number) => `description_${languageId}`;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

const buildRows = (
  languages: ManageTranslationLanguage[],
  translations: ManageTranslationItem[]
): ManageTranslationItem[] => {
  if (languages.length === 0) {
    return translations.map((translation) => ({
      ...translation,
      translation_name: translation.translation_name || "",
    }));
  }

  return languages.map((language) => {
    const existing = translations.find((t) => t.language_id === language.id);
    return {
      translation_id: existing?.translation_id || null,
      translation_name: existing?.translation_name || "",
      translation_description: existing?.translation_description || "",
      language_id: language.id,
      language_code: language.language_code,
      language_name: language.name,
    };
  });
};

/**
 * Common "Manage translations" modal (project modal style: #ba2156 header, 120px footer buttons).
 * Data loading/saving is left to the caller through `translations`, `onSave` and `onAutoTranslate`.
 */
const ManageTranslationModal: React.FC<ManageTranslationModalProps> = ({
  open,
  title,
  subtitle,
  languages,
  translations,
  loading = false,
  ready = true,
  requireAll = false,
  withDescription = false,
  fallbackSourceText,
  onCancel,
  onSave,
  onAutoTranslate,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const openRef = useRef(open);
  const requestRef = useRef(0);
  const hydratedRef = useRef(false);

  const rows = useMemo(() => buildRows(languages, translations), [languages, translations]);

  const resetState = useCallback(() => {
    requestRef.current += 1;
    setSaving(false);
    setAutoTranslating(false);
    hydratedRef.current = false;
    form.resetFields();
  }, [form]);

  useEffect(() => {
    openRef.current = open;
    if (!open) resetState();
  }, [open, resetState]);

  // Hydrate once per open; later changes of `languages`/`translations` (e.g. SWR revalidation)
  // only fill fields that are still empty so the admin's edits are never overwritten.
  useEffect(() => {
    if (!open || !ready) return;

    const values: Record<string, string> = {};
    rows.forEach((row) => {
      const name = fieldName(row.language_id);
      if (!hydratedRef.current || form.getFieldValue(name) === undefined) {
        values[name] = row.translation_name;
      }
      const descName = descriptionFieldName(row.language_id);
      if (withDescription && (!hydratedRef.current || form.getFieldValue(descName) === undefined)) {
        values[descName] = row.translation_description || "";
      }
    });

    if (Object.keys(values).length > 0) form.setFieldsValue(values);
    hydratedRef.current = true;
  }, [form, open, ready, rows, withDescription]);

  const handleCancel = () => {
    openRef.current = false;
    resetState();
    onCancel();
  };

  const handleAutoTranslate = async () => {
    if (!onAutoTranslate) return;

    const values = form.getFieldsValue();
    const isFilled = (item: ManageTranslationItem) =>
      String(values[fieldName(item.language_id)] || "").trim().length > 0;
    const filled = rows.filter(isFilled);
    const isDescFilled = (item: ManageTranslationItem) =>
      String(values[descriptionFieldName(item.language_id)] || "").trim().length > 0;
    const missing = rows.filter((item) => !isFilled(item));
    const missingDesc = withDescription ? rows.filter((item) => !isDescFilled(item)) : [];

    if (missing.length === 0 && missingDesc.length === 0) {
      toast.info(t("common.translation_modal.all_filled"));
      return;
    }

    const source = filled.find((item) => item.language_code === "en") || filled[0];
    const sourceLanguageCode =
      source?.language_code || localStorage.getItem("language") || "en";
    const sourceText = source
      ? String(values[fieldName(source.language_id)]).trim()
      : String(fallbackSourceText || "").trim();

    if (!sourceText) {
      toast.warning(t("common.translation_modal.need_source"));
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    try {
      setAutoTranslating(true);
      const fillValues: Record<string, string> = {};

      if (missing.length > 0) {
        const translated = await onAutoTranslate(sourceText, sourceLanguageCode);
        if (!openRef.current || requestRef.current !== requestId) return;
        missing.forEach((item) => {
          const text = translated[item.language_code];
          if (text) fillValues[fieldName(item.language_id)] = text;
        });
      }

      if (missingDesc.length > 0) {
        const descSource =
          rows.find((item) => item.language_code === sourceLanguageCode && isDescFilled(item)) ||
          rows.find(isDescFilled);
        if (descSource) {
          const descText = String(values[descriptionFieldName(descSource.language_id)]).trim();
          const translatedDesc = await onAutoTranslate(descText, descSource.language_code);
          if (!openRef.current || requestRef.current !== requestId) return;
          missingDesc.forEach((item) => {
            const text = translatedDesc[item.language_code];
            if (text) fillValues[descriptionFieldName(item.language_id)] = text;
          });
        }
      }

      if (Object.keys(fillValues).length === 0) {
        toast.warning(t("common.translation_modal.none_returned"));
        return;
      }

      form.setFieldsValue(fillValues);
      toast.success(t("common.translation_modal.auto_done"));
    } catch (error) {
      if (!openRef.current || requestRef.current !== requestId) return;
      console.error("Error auto translating:", error);
      toast.error(getErrorMessage(error, t("common.translation_modal.auto_failed")));
    } finally {
      if (openRef.current && requestRef.current === requestId) {
        setAutoTranslating(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave(
        rows.map((item) => ({
          ...item,
          translation_name: values[fieldName(item.language_id)] || "",
          ...(withDescription && {
            translation_description: values[descriptionFieldName(item.language_id)] || "",
          }),
        }))
      );
      handleCancel();
    } catch (error) {
      // Validation errors are shown inline; the caller reports save failures itself.
      if (!(typeof error === "object" && error !== null && "errorFields" in error)) {
        console.error("Error saving translations:", error);
      }
    } finally {
      if (openRef.current) setSaving(false);
    }
  };

  const disabled = loading || rows.length === 0;

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      width={700}
      title={null}
      closable={false}
      styles={{ content: { borderRadius: "12px", overflow: "hidden", padding: 0 } }}
      footer={
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          px={3}
          py={2}
          borderTop="1px solid var(--app-border-light)"
        >
          <Box>
            {onAutoTranslate && (
              <Button
                icon={<TranslationOutlined />}
                loading={autoTranslating}
                onClick={handleAutoTranslate}
                disabled={disabled}
              >
                {t("common.translation_modal.auto_translate")}
              </Button>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <Button style={{ width: "120px" }} onClick={handleCancel} disabled={saving}>
              {t("btn.cancel")}
            </Button>
            <Button
              type="primary"
              style={{ width: "120px", background: "#ba2156", border: "none" }}
              loading={saving}
              onClick={handleSave}
              disabled={disabled}
            >
              {t("btn.save")}
            </Button>
          </Box>
        </Box>
      }
    >
      <Box bgcolor="#ba2156" color="#fff" px={3} py={2}>
        <Box fontSize="18px" fontWeight={700} lineHeight={1.3}>
          {title ?? t("common.translation_modal.title")}
        </Box>
        {subtitle && (
          <Box fontSize="12px" sx={{ opacity: 0.85 }}>
            {subtitle}
          </Box>
        )}
      </Box>
      <Box px={3} py={3}>
        <Form form={form} layout="vertical" className="translation-form">
          <div
            style={{
              padding: "16px",
              background: "var(--app-bg)",
              borderRadius: "6px",
              border: "1px solid var(--app-border-light)",
            }}
          >
            <Table
              dataSource={rows}
              rowKey={(record) => String(record.language_id)}
              pagination={false}
              bordered
              loading={loading}
              columns={[
                {
                  title: t("common.translation_modal.language"),
                  dataIndex: "language_code",
                  key: "language_code",
                  width: "30%",
                  render: (_, record) => (
                    <span style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                      {record.language_code}
                      {record.language_name && ` (${record.language_name})`}
                    </span>
                  ),
                },
                {
                  title: t("common.translation_modal.translation"),
                  key: "translation",
                  width: "70%",
                  render: (_, record) => (
                    <>
                    <Form.Item
                      name={fieldName(record.language_id)}
                      style={{ margin: 0 }}
                      rules={
                        requireAll
                          ? [{ required: true, message: t("common.translation_modal.required") }]
                          : []
                      }
                    >
                      <Input placeholder={t("common.translation_modal.enter_translation", { code: record.language_code })} />
                    </Form.Item>
                    {withDescription && (
                      <Form.Item
                        name={descriptionFieldName(record.language_id)}
                        style={{ margin: "8px 0 0" }}
                      >
                        <Input.TextArea
                          rows={2}
                          placeholder={t("common.translation_modal.enter_description", { code: record.language_code })}
                        />
                      </Form.Item>
                    )}
                    </>
                  ),
                },
              ]}
            />
          </div>
        </Form>
      </Box>
    </Modal>
  );
};

export default ManageTranslationModal;
